export const histologyModule = {
  id: "histology",
  title: "Histology of the Adrenal Glands",
  sections: [
    {
      type: "text",
      content:
        '<p>To recap, the adrenal glands are composed of: <ul><li>The <b class="purple-term">adrenal capsule</b></li><li>The <b class="purple-term">adrenal cortex</b><ul><li>Zona Glomerulosa</li><li>Zona Fasciculata</li><li>Zona Reticularis</li></ul></li><li>The <b class="purple-term">adrenal medulla</b></li></ul></p><p>Let\'s explore the histology of each of these layers in more detail.</p>',
      image: "/images/adrenalglands/adrenalglandlayers.png",
    },
    {
      type: "text",
      content: ` <h3>Adrenal Capsule</h3>
      <ul>
        <li>Is usually <b>thin</b>, measuring about 0.5 to 1 mm in thickness, but varies considerably in thickness.</li>
        <li>Is <span class="glossary-term">hypocellular<span class="tooltip">Fewer cells than normal</span></span> and made up of <span class="glossary-term">hyalinized collagen<span class="tooltip">Collagen that has been transformed into a glassy, translucent substance due to aging or disease processes. Denser and less organized.</span></span> bundles and <span class="glossary-term">elastin fibers<span class="tooltip">Elastic fibers provide elasticity and resilience to tissues.</span></span>.</li>
        <li>Is sometimes seen fused with the capsule of the kidney or the capsule of the liver.</li>
        <li>Is penetrated by <b>blood vessels</b>, <b>lymphatics</b> and <b>nerve fibers</b> that supply the adrenal gland.</li>    
     </ul>  `,
      image: "/images/adrenalglands/adrenalglandhistocapsule.png",
    },
    {
      type: "question",
      text: "What type of fibers make up the adrenal capsule?",
      options: ["Collagen and elastin fibers", "Muscle fibers", "Nerve fibers"],
      correct: 0,
      explanation:
        "The adrenal capsule is made up of hyalinized collagen bundles and elastin fibers.",
    },
    {
      type: "question",
      text: "To which organ capsule is the adrenal capsule sometimes fused?",
      options: [
        "Kidney and spleen capsule",
        "Liver and spleen capsule",
        "Kidney and Liver capsule",
      ],
      correct: 2,
      explanation:
        "The adrenal capsule is sometimes seen fused with the capsule of the kidney or the capsule of the liver.",
    },
    {
      type: "question",
      text: "What does it mean when a tissue is described as 'hyalinized collagen'?",
      options: [
        "Collagen that is elastic and resilient",
        "Collagen that has been transformed into a glassy, translucent substance",
        "Collagen that is highly cellular",
      ],
      correct: 1,
      explanation:
        "Hyalinized collagen has been transformed into a glassy, translucent substance due to aging or disease processes. It is denser and less organized than normal collagen.",
    },
    {
      type: "text",
      content: `
      <h3>Adrenal Cortex</h3>
      <p>The adrenal cortex is about 1 mm in thickness and consists of three distinct layers, made out of 
      <span class="glossary-term">parenchymal cell types<span class="tooltip">the 
      functional cells of an organ, cells with specialized functions</span></span>, 
      that are arranged in zones/layers:</p>
      <ul>
        <li><strong>Zona Glomerulosa:</strong> The outermost layer. 
        <ul>
        <li>The layer is typically diffuse during childhood and adulescence, but becomes discontinuous in adults.</li>
        <li>Cells are shaped in rounded clusters, named for their <b>"ball-like" arrangement</b> - 
        hence "glomerulosa" and are supported by small amount of fibrovascular stroma. </li>
        <li>Cells are columnar and stain either <span class="glossary-term">acidophilic<span class="tooltip">staining readily with acid dyes, typically pink or red</span></span> or <span class="glossary-term">amphophilic<span class="tooltip">staining with both acid (pink or red) and basic (blue or purple) dyes, giving a color that is in between pink and blue</span></span> with small amounts of vacules. 
        Nucleus can be round, ellipsoidal and elongated and with a longitudinal groove.</li>
        <li>Nuclear to cytoplasmic ration: High (meaning the nucleus takes up a large portion of the cell compared to the cytoplasm).</li>
      </li>
        </ul></li>
        <li><strong>Zona Fasciculata:</strong> The middle layer. 
        <ul>
        <li>Is the <b>thickest layer</b> of the adrenal cortex.</li>
        <li>Cells are large and have distinct boarders (cell membrane).</li>
        <li>Cells are arranged in long two-cell straight columns, giving this layer its name. </li>
        <li>The nucleus is centrally placed and tends to be <span class="glossary-term">vesicular<span class="tooltip">having a clear, bubble-like appearance due to dispersed chromatin</span></span>
        with small nucleolus</li>
       <li>Nuclear to cytoplasmic ratio: Low (meaning the cytoplasm takes up a large portion of the cell compared to the nucleus).</li>
    
        </ul>
        </li>
        <li><strong>Zona Reticularis:</strong> The innermost layer. 
        <ul>
        <li>Cells have a more irregular, net-like arrangement, hence the name "reticularis", and are seperated by dialated capillaries. </li>
        <li>The cytoplasm stains <span class="glossary-term">acidophilic<span class="tooltip">staining readily with acid dyes, typically pink or red</span></span> and has a granular appearance.</li>
     <li>The cells that are close to the medulla, tend to contain <span class="glossary-term">lipofuscin pigment <span class="tooltip">a yellow-brown pigment that accumulates with age</span></span>, 
     which is the reason for the darker staining of this layer compared to the other two layers of the cortex
     and can be seen macroscopically as a thin brown line at the corticomedullary junction.</li>
        </ul>
      `,
      image: "/images/adrenalglands/adrenalglandhistocortex.png",
    },
    {
      type: "question",
      text: "Which layer of the adrenal cortex is responsible for producing glucocorticoids like cortisol?",
      options: ["Zona Glomerulosa", "Zona Fasciculata", "Zona Reticularis"],
      correct: 1,
      explanation:
        "The Zona Fasciculata, the middle and thickest layer of the adrenal cortex, produces glucocorticoids like cortisol.",
    },
    {
      type: "question",
      text: "How would one describe the cells of the zona glomerulosa?",
      options: [
        "Arranged in long, straight columns",
        "Arranged in rounded clusters",
        "Arranged in a net-like pattern",
      ],
      correct: 1,
      explanation:
        "The cells of the zona glomerulosa are arranged in rounded clusters, named for their 'ball-like' arrangement - hence 'glomerulosa'.",
    },
    {
      type: "question",
      text: "How would you describe the cells of the zona fasciculata?",
      options: [
        "Large cells with distinct cell membranes",
        "Darkly staining with a net-like arrangement",
        "Arranged in rounded clusters",
      ],
      correct: 0,
      explanation:
        "The cells of the zona fasciculata are large and have distinct cell membranes. They are arranged in long two-cell straight columns.",
    },
    {
      type: "question",
      text: "In which layer of the adrenal cortex are the cells arranged in a net-like pattern?",
      options: ["Zona Glomerulosa", "Zona Fasciculata", "Zona Reticularis"],
      correct: 2,
      explanation:
        "The Zona Reticularis has cells arranged in a more irregular, net-like pattern, hence the name 'reticularis'.",
    },
    {
      type: "question",
      text: "Which layer of the adrenal cortex has cells that form long, straight columns?",
      options: ["Zona Glomerulosa", "Zona Fasciculata", "Zona Reticularis"],
      correct: 1,
      explanation:
        "The Zona Fasciculata has cells arranged in long two-cell straight columns, giving this layer its name.",
    },
    {
      type: "text",
      content: `
      <h3>Adrenal Medulla</h3>
      <ul>
        <li>Located at the center of the adrenal gland.</li>
        <li>Not more than 2 mm in thickness.</li>
        <li>Composed of chromaffin cells (somtimes called pheochromocytes or medullary cells), which are modified postganglionic sympathetic neurons
        (remember that they are affected by preganglionic neurons that release acetylcholine, and 
        they in turn release adrenaline and noradrenaline (just like postganglionic neurons)).</li>
        <li>The cells are arranged in clusters or short trabeculae, surrounded by a rich network of sinusoids (specialized blood vessels) and fibrous tissue.</li>
        <li>The cells are moderately large and have a polygonal or columnar shape.</li>
        <li>They stain basophilic (dark purple).</li>
        <li>The nucleus varies in size, shape and in location within the cell.</li>
        <li>Within the adrenal medulla, there are also <b>sympathetic ganglion cells</b> that are part of the autonomic nervous system.
        That are round or polygonal in shape, with a prominent nuclei, often associated with a nerve.</li>
        <li>Last but not least, there are <b>blood vessels</b> that facilitate the rapid release of hormones into the bloodstream during stress responses
        and immune cells clusters of plasma cells and lymphocytes (often around blood vesselsad).</li>
      </ul>
      `,
      image: "/images/adrenalglands/adrenalglandhistomedulla.png",
    },
    {
      type: "question",
      text: "What type of cells are found in the adrenal medulla that release adrenaline and noradrenaline?",
      options: ["Spongiocytes", "Chromaffin cells", "Cortical cells"],
      correct: 1,
      explanation:
        "The adrenal medulla is composed of chromaffin cells (sometimes called pheochromocytes or medullary cells), which are modified postganglionic sympathetic neurons that release adrenaline and noradrenaline.",
    },
    {
      type: "question",
      text: "What type of staining do chromaffin cells in the adrenal medulla exhibit?",
      options: [
        "Acidophilic (pink/red)",
        "Basophilic (dark purple)",
        "Amphophilic (pink and blue)",
      ],
      correct: 1,
      explanation:
        "Chromaffin cells in the adrenal medulla stain basophilic, appearing dark purple.",
    },
    {
      type: "question",
      text: "What other cell types are found in the adrenal medulla besides chromaffin cells?",
      options: [
        "Spongiocytes and cortical cells",
        "Sympathetic ganglion cells and immune cells",
        "Fibroblasts and superman cells",
      ],
      correct: 1,
      explanation:
        "Besides chromaffin cells, the adrenal medulla also contains sympathetic ganglion cells and immune cells such as plasma cells and lymphocytes.",
    },
    {
      type: "question",
      text: "Which pigment accumulates in cells of the zona reticularis near the medulla, and what does it indicate?",
      options: [
        "Haemosiderin — indicating past haemorrhage",
        "Lipofuscin — a wear-and-tear pigment that accumulates with age",
        "Melanin — produced by neural crest-derived cells",
        "Bilirubin — from breakdown of red blood cells",
      ],
      correct: 1,
      explanation:
        "Cells of the zona reticularis (particularly near the corticomedullary junction) accumulate lipofuscin, a yellow-brown 'wear-and-tear' pigment produced by lipid peroxidation. It gives this layer a darker appearance on H&E and can be seen macroscopically as a thin brown line at the corticomedullary junction. Its accumulation increases with age and is a marker of oxidative stress within the cell.",
    },
    {
      type: "text",
      content: `
        <h3>Why does the Zona Fasciculata appear pale on H&E?</h3>
        <p>A distinctive and frequently tested feature of the zona fasciculata is its <b class="purple-term">pale, "clear cell" appearance</b> on H&E-stained sections. This is because the cells are <b>lipid-rich</b> — their cytoplasm is packed with <b class="purple-term">cholesterol droplets and lipid vesicles</b>, which are the substrate for steroid (cortisol) synthesis.</p>
        <p>During routine histological processing, tissues are dehydrated with alcohol and embedded in paraffin wax. <b>Lipids dissolve in these organic solvents</b>, leaving behind empty spaces (vacuoles) in the cytoplasm. On the final H&E slide, the cells look pale or "washed out" — this is the hallmark appearance.</p>
        <p>These lipid-laden cells are sometimes called <b class="purple-term">"spongiocytes"</b>, because their vacuolated cytoplasm gives them a sponge-like look.</p>
        <div class="highlight-box">
          <b>Zona fasciculata cells:</b> Lipid-rich cytoplasm (cholesterol for steroid synthesis) → Lipids dissolve in processing → Pale/clear "spongiocyte" appearance on H&E<br/>
          <b>Contrast:</b> Zona glomerulosa and reticularis contain less lipid → stain darker (more eosinophilic)
        </div>
      `,
      image: "/images/adrenalglands/adrenalglandspongiocytes.png",
    },
    {
      type: "question",
      text: "On an H&E-stained section of the adrenal gland, which zone appears palest (most 'clear') and why?",
      options: [
        "Zona glomerulosa — because its cells have the highest nuclear-to-cytoplasmic ratio",
        "Zona fasciculata — because its lipid-rich cytoplasm dissolves during processing, leaving empty vacuoles",
        "Zona reticularis — because lipofuscin pigment bleaches the cytoplasm",
        "Adrenal medulla — because chromaffin cells contain water-soluble catecholamines",
      ],
      correct: 1,
      explanation:
        "The zona fasciculata appears pale/clear on H&E because its cells (spongiocytes) are loaded with cholesterol lipid droplets used for cortisol synthesis. During routine tissue processing, lipids are dissolved by organic solvents, leaving empty vacuoles in the cytoplasm. In contrast, the zona glomerulosa and reticularis are more lipid-poor and stain darker (more eosinophilic).",
    },
    {
      type: "text",
      content: `
        <h3>The Chromaffin Reaction and Chromaffin Granules</h3>
        <p>The term <b class="purple-term">"chromaffin"</b> comes from "chromium affinity." When adrenal medullary tissue is fixed with <b class="purple-term">potassium dichromate</b> (a chromium salt), the catecholamines inside the secretory granules are oxidised, producing a <b>brown-yellow colour</b> — this is the <b class="purple-term">chromaffin reaction</b>. It was historically used to identify catecholamine-secreting cells.</p>

        <h3>Chromaffin granules</h3>
        <p>Chromaffin cells contain large numbers of <b class="purple-term">dense-core secretory granules</b> (~200 nm diameter) within their cytoplasm. These granules store:</p>
        <ul>
          <li><b class="purple-term">Catecholamines</b> (adrenaline or noradrenaline — adrenaline-secreting and noradrenaline-secreting cells are distinct subtypes)</li>
          <li><b class="purple-term">Chromogranin A</b> — a co-secreted protein that helps package catecholamines and is released along with them into the bloodstream</li>
          <li>ATP, neuropeptides and enkephalins (co-transmitters)</li>
        </ul>
        <div class="highlight-box">
          <b>Chromogranin A</b> is an important clinical marker: it is elevated in the serum of patients with <b>phaeochromocytoma</b> (and other neuroendocrine tumours), and can be used as a tumour marker. Immunohistochemistry for chromogranin A and <b>synaptophysin</b> is used to confirm the neuroendocrine nature of these tumours on biopsy.
        </div>
        <p>On electron microscopy, the two types of chromaffin cell can sometimes be distinguished: <b>adrenaline-secreting cells</b> tend to have less electron-dense (lighter) granules, while <b>noradrenaline-secreting cells</b> have highly electron-dense (darker) granules with an eccentric dense core.</p>
      `,
    },
    {
      type: "question",
      text: "A 35-year-old patient has episodic hypertension, headaches and sweating. An adrenal mass is identified on CT. A biopsy is sent for immunohistochemistry. Which two markers would confirm a phaeochromocytoma?",
      options: [
        "Oestrogen receptor and progesterone receptor",
        "Chromogranin A and synaptophysin",
        "Cytokeratin and vimentin",
        "CD20 and CD3",
      ],
      correct: 1,
      explanation:
        "Phaeochromocytoma is a neuroendocrine tumour of chromaffin cells. Neuroendocrine tumours are characteristically positive for chromogranin A (a secretory granule protein co-released with catecholamines) and synaptophysin (a synaptic vesicle membrane protein). These two markers confirm neuroendocrine differentiation. Cytokeratin and vimentin are used for carcinomas, CD20/CD3 for lymphomas, and hormone receptors for breast/gynaecological tumours.",
    },
    {
      type: "text",
      content: `
        <h3>Blood Supply Arrangement — The Corticomedullary Portal System</h3>
        <p>The arrangement of blood flow through the adrenal gland is clinically important because it explains why the adrenal medulla can synthesise adrenaline (but peripheral sympathetic neurons cannot).</p>
        <p>Blood enters the adrenal gland via three groups of arteries (superior, middle, inferior suprarenal arteries) and flows inward through the gland:</p>
        <ol>
          <li>Arterial blood enters the <b class="purple-term">subcapsular arteriolar plexus</b> beneath the capsule</li>
          <li>Blood flows centripetally (inward) through <b class="purple-term">sinusoids</b> in the cortex — bathing the zona glomerulosa, then fasciculata, then reticularis cells as it flows inward</li>
          <li>By the time blood reaches the <b class="purple-term">medulla</b>, it has already passed through the entire cortex and is now <b>enriched with cortisol</b> at supraphysiological concentrations</li>
          <li>Blood exits via the <b class="purple-term">central adrenal vein</b> (right drains to IVC, left to left renal vein)</li>
        </ol>
        <div class="highlight-box">
          <b>Why this matters:</b> The PNMT enzyme (needed to convert noradrenaline → adrenaline) requires high cortisol concentrations to be expressed. Because medullary chromaffin cells are bathed in cortisol-rich blood from the cortex, they express PNMT and can make adrenaline. Peripheral sympathetic neurons receive only systemic (low) cortisol levels — they cannot express PNMT and therefore cannot convert noradrenaline to adrenaline.
        </div>
        <p>This sinusoidal arrangement also explains why adrenal vein <b class="purple-term">sampling</b> (AVS) is used in Conn's syndrome — blood drawn from each adrenal vein can be analysed for cortisol (to confirm correct catheter placement) and aldosterone (to determine which side is overproducing).</p>
      `,
    },
    {
      type: "question",
      text: "Blood flowing through the adrenal sinusoids travels in which direction?",
      options: [
        "From the medulla outward to the capsule (centrifugally)",
        "From the capsule inward to the medulla (centripetally)",
        "From the medulla to the zona glomerulosa only",
        "Bidirectionally through anastomosing sinusoids",
      ],
      correct: 1,
      explanation:
        "Blood enters via arteries beneath the capsule and flows centripetally (inward) through sinusoids: capsule → zona glomerulosa → zona fasciculata → zona reticularis → medulla → central adrenal vein. This means the medulla receives cortisol-enriched blood from the cortex, which is essential for PNMT expression and adrenaline synthesis.",
    },
    {
      type: "flashcard",
      front: "Compare the histological features of each adrenal cortical zone and the medulla.",
      back: `<table style="width:100%; border-collapse: collapse; font-size: 0.85em;">
        <tr style="background:#f3f4f6">
          <th style="padding:4px 6px; text-align:left">Zone</th>
          <th style="padding:4px 6px">Cell arrangement</th>
          <th style="padding:4px 6px">Staining</th>
          <th style="padding:4px 6px">Key feature</th>
        </tr>
        <tr>
          <td style="padding:4px 6px"><b>Glomerulosa</b></td>
          <td style="padding:4px 6px; text-align:center">Rounded clusters ("balls")</td>
          <td style="padding:4px 6px; text-align:center">Acidophilic / amphophilic; dark</td>
          <td style="padding:4px 6px; text-align:center">High N:C ratio; small lipid vacuoles; discontinuous in adults</td>
        </tr>
        <tr style="background:#f9fafb">
          <td style="padding:4px 6px"><b>Fasciculata</b></td>
          <td style="padding:4px 6px; text-align:center">Long straight columns (2-cell wide)</td>
          <td style="padding:4px 6px; text-align:center">Pale/clear ("spongiocytes")</td>
          <td style="padding:4px 6px; text-align:center">Lipid-rich (cholesterol for cortisol); low N:C ratio; thickest zone</td>
        </tr>
        <tr>
          <td style="padding:4px 6px"><b>Reticularis</b></td>
          <td style="padding:4px 6px; text-align:center">Irregular net-like cords</td>
          <td style="padding:4px 6px; text-align:center">Acidophilic; granular; darker</td>
          <td style="padding:4px 6px; text-align:center">Lipofuscin pigment near medulla → brown line at junction</td>
        </tr>
        <tr style="background:#f9fafb">
          <td style="padding:4px 6px"><b>Medulla</b></td>
          <td style="padding:4px 6px; text-align:center">Clusters / short trabeculae</td>
          <td style="padding:4px 6px; text-align:center">Basophilic (dark purple)</td>
          <td style="padding:4px 6px; text-align:center">Chromaffin cells; dense-core granules; chromaffin reaction (brown with Cr salts); chromogranin A +ve</td>
        </tr>
      </table>`,
      hint: "Glomerulosa = balls | Fasciculata = columns + pale spongiocytes | Reticularis = net + lipofuscin | Medulla = chromaffin",
    },
  ],
};
